import React from 'react';
import { Feature } from './core/enums/features';
import { BaseRoute } from './core/models/base-routes';
import { DashboardRoutes } from './dashboard/dashboard.routes';
import { LandingRoutes } from './landing/landing.routes';
import { HardwareRoutes } from './products/hardware/hardware.routes';
import { InsuranceRoutes } from './products/insurance/insurance.routes';
import { InternetRoutes } from './products/internet/internet.routes';
import { SubscriptionsRoutes } from './products/subscription/subscription.routes';
import { PremiumRoutes, RewardRoutes } from './rewards/rewards.routes';

export const routes: BaseRoute[] = [
  {
    path: '',
    title: '',
    name: 'landing',
    feature: Feature.Landing,
    component: React.lazy(() => import('./landing/landing')),
    children: LandingRoutes,
    icon: 'icon-dashboard',
    hideFromMainMenu: true,
  },
  {
    path: '/dashboard/*',
    title: 'Dashboard',
    name: 'dashboard',
    feature: Feature.Dashboard,
    component: React.lazy(() => import('./dashboard/dashboard')),
    children: DashboardRoutes,
    icon: 'icon-dashboard',
    protected: true,
  },
  {
    path: '/rewards',
    title: 'My Rewards',
    name: 'myRewards',
    feature: Feature.Rewards,
    component: React.lazy(() => import('./rewards/rewards-landing/rewards-landing')),
    icon: 'icon-rewards_available_v2',
    protected: false,
    children: RewardRoutes,
  },
  {
    path: '/premium-status',
    title: '100% Premium',
    name: 'myPremium',
    feature: Feature.Premium,
    component: React.lazy(() => import('./rewards/rewards-landing/rewards-landing')),
    icon: 'icon-rewards_available_v2',
    protected: true,
    children: PremiumRoutes,
  },
  {
    title: 'My Account',
    name: 'myAccount',
    feature: Feature.Account,
    component: React.lazy(() => import('./account/account')),
    icon: 'icon-manage-profile',
    protected: true,
    children: [
      {
        path: '/account/view-balance',
        title: 'View balance & pay',
        name: 'viewBalance',
        feature: Feature.ViewBalance,
        component: React.lazy(() => import('./account/view-balance/view-balance')),
      },
      {
        path: '/account/billing-explained',
        title: 'View balance & pay',
        name: 'billingExplained',
        feature: Feature.BillingExplained,
        component: React.lazy(() => import('./account/billing-explained/billing-explained')),
      },
      {
        path: '/account/transactions',
        title: 'Transaction History',
        name: 'transactionHistory',
        feature: Feature.TransactionHistory,
        component: React.lazy(() => import('./account/transaction-history/transaction-history')),
      },
      {
        path: '/account/monthly-statements',
        title: 'Monthly Statements',
        name: 'monthlyStatements',
        feature: Feature.MonthlyStatements,
        component: React.lazy(() => import('./account/monthly-statements/monthly-statements')),
      },
      {
        path: '/account/update-payment-method',
        title: 'Update Method of Payment',
        name: 'updatePaymentMethod',
        feature: Feature.PaymentMethods,
        component: React.lazy(() => import('./account/update-method-of-payment/containers/standard-container')),
      },
      // NOTE: Added for the Rapyd payment POC
      {
        path: '/account/rapyd-payment',
        title: 'Rapyd Payment',
        name: 'rapydPayment',
        protected: true,
        hideFromMainMenu: true,
        feature: Feature.PaymentMethods,
        component: React.lazy(() => import('./account/rapyd-payment-poc/rapyd-payment')),
      },
      {
        path: '/account/rapyd-payment/success',
        title: 'Rapyd Payment Success',
        name: 'rapydPaymentSuccess',
        protected: true,
        hideFromMainMenu: true,
        feature: Feature.PaymentMethods,
        component: React.lazy(() => import('./account/rapyd-payment-poc/outcomes/success')),
      },
      {
        path: '/account/rapyd-payment/fail',
        title: 'Rapyd Payment Fail',
        name: 'rapydPaymentFail',
        protected: true,
        hideFromMainMenu: true,
        feature: Feature.PaymentMethods,
        component: React.lazy(() => import('./account/rapyd-payment-poc/outcomes/fail')),
      },
      {
        path: '/account/rapyd-payment/redirect',
        title: 'Rapyd Payment Redirect',
        name: 'rapydPaymentRedirect',
        protected: true,
        hideFromMainMenu: true,
        feature: Feature.PaymentMethods,
        component: React.lazy(() => import('./account/rapyd-payment-poc/outcomes/redirect')),
      },
      {
        path: '/account/recurring-payment/*',
        title: 'Recurring Payment',
        name: 'recurringPayment',
        protected: true,
        hideFromMainMenu: true,
        feature: Feature.RecurringPayment,
        children: [
          {
            path: '/account/recurring-payment/success',
            title: 'Recurring Payment',
            name: 'recurringPaymentSuccess',
            feature: Feature.RecurringPayment,
            component: React.lazy(() => import('./account/recurring-payment/recurring-payment-success')),
          }
        ],
        component: React.lazy(() => import('./account/recurring-payment/recurring-payment')),
      },
      // {
      //   path: '/account/recurring-payment/success',
      //   title: 'Recurring Payment',
      //   name: 'recurringPaymentSuccess',
      //   feature: Feature.RecurringPayment,
      //   component: React.lazy(() => import('./account/recurring-payment/recurring-payment-success')),
      // },
      {
        path: '/profile/privacy-information',
        title: 'User Preferences',
        name: 'profilePreferences',
        feature: Feature.ProfilePreferences,
        component: React.lazy(() => import('./profile/preferences-and-privacy/privacy-information')),
      },
    ],
  },
  {
    path: '/products',
    title: 'My Products',
    name: 'myProducts',
    feature: Feature.Products,
    component: React.lazy(() => import('./products/subscription/subscription-detail/subscription-detail')),
    icon: 'icon-streaming-devices',
    protected: true,
    children: [
      {
        path: '/products/activate',
        title: 'Activate DStv',
        name: 'activate',
        feature: Feature.Activate,
        component: React.lazy(() => import('./products/subscription/subscription-detail/subscription-detail')),
      },
      {
        path: '/all-products',
        title: 'All products',
        name: 'products',
        feature: Feature.AllProducts,
        component: React.lazy(() => import('./products/subscription/subscription-detail/subscription-detail')),
      },
      {
        path: '/subscription/:productId',
        title: 'Subscriptions',
        name: 'subscriptions',
        feature: Feature.Subscriptions,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/subscription/subscription-detail/subscription-detail')),
        protected: true,
      },
      {
        path: '/subscription-details/select',
        title: 'Subscriptions',
        name: 'subscriptions',
        feature: Feature.SelectManage,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/subscription/select-manage/select-manage')),
        protected: true,
      },
      {
        path: '/subscription/decoder-switch-to-contract/:productId/:agreementId/:smartcardNumber',
        title: 'Subscriptions',
        name: 'subscriptions',
        feature: Feature.Subscriptions,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/contracts/decoder-switch-to-contract/decoder-contract-switch')),
        protected: true,
      },
      {
        path: '/subscription/switch-to-contract/:productId/:agreementId',
        title: 'Subscriptions',
        name: 'switch-to-contract',
        feature: Feature.Subscriptions,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/contracts/stream-switch-to-contract/stream-switch-to-contract')),
        protected: true,
        isOverlay: true,
      },
      {
        path: '/subscription/stream-contract-renewal/:productId/:agreementId/:bundleId',
        title: 'Subscriptions',
        name: 'stream-contract-renewal',
        feature: Feature.Subscriptions,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/contracts/stream-contract-renewal/stream-contract-renewal')),
        protected: true,
        isOverlay: true,
      },
      {
        path: '/subscription/contract-expiration-renewal/:productId/:agreementId/:bundleId',
        title: 'Subscriptions',
        name: 'contract-expiration-renewal',
        feature: Feature.Subscriptions,
        hideFromMainMenu: true,
        component: React.lazy(
          () => import('./products/contracts/contract-expiration-renewal/contract-expiration-renewal')
        ),
        protected: true,
        isOverlay: true,
      },
      {
        path: '/subscription/:productId/add-on/:addonId',
        title: 'Subscriptions',
        name: 'subscriptions',
        feature: Feature.Subscriptions,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/subscription/add-on-detail/add-on-detail')),
        protected: true,
      },
      {
        path: '/subscriptions/*',
        title: 'Subscriptions',
        name: 'subscriptions',
        feature: Feature.Subscriptions,
        component: React.lazy(() => import('./products/subscription/subscription')),
        children: SubscriptionsRoutes,
        protected: true,
      },
      {
        path: '/boxoffice',
        title: 'BoxOffice',
        name: 'boxOffice',
        feature: Feature.BoxOffice,
        component: React.lazy(() => import('./products/on-demand-services/on-demand-services')),
        protected: true,
      },
      {
        path: '/boxoffice/rental-history',
        title: 'Rental History',
        name: 'rentalHistory',
        feature: Feature.RentalHistory,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/rental-history/rental-history')),
        protected: true,
      },
      {
        path: '/hardware/*',
        title: 'Hardware',
        name: 'hardware',
        feature: Feature.Products,
        children: HardwareRoutes,
        component: React.lazy(() => import('./products/hardware/hardware')),
        protected: true,
      },
      {
        path: '/hardware-details/:agreementId/:smartCardNumber',
        title: 'Hardware',
        name: 'hardware',
        feature: Feature.Products,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/hardware/device-details/device-details')),
        protected: true,
      },
      {
        path: '/internet/*',
        title: 'Internet',
        name: 'internet',
        children: InternetRoutes,
        feature: Feature.Internet,
        component: React.lazy(() => import('./products/internet/internet')),
        protected: true,
      },
      {
        path: '/internet-details/:agreementId',
        title: 'Internet',
        name: 'internet',
        feature: Feature.Internet,
        hideFromMainMenu: true,
        component: React.lazy(() => import('./products/internet/internet-details/internet-details')),
        protected: true,
      },
      {
        path: '/insurance/*',
        title: 'Insurance',
        name: 'insurance',
        feature: Feature.Insurance,
        children: InsuranceRoutes,
        component: React.lazy(() => import('./products/insurance/insurance-landing')),
        protected: true,
      },
      {
        path: '/insurance-details/:productType/:agreementId',
        title: 'Insurance',
        name: 'insurance',
        feature: Feature.Insurance,
        component: React.lazy(() => import('./products/insurance/insurance-details/insurance-details')),
        hideFromMainMenu: true,
        protected: true,
      },
      {
        path: '/insurance-details/:productType/:agreementId/:smartcardNumber',
        title: 'Insurance',
        name: 'insurance',
        feature: Feature.Insurance,
        component: React.lazy(() => import('./products/insurance/insurance-details/insurance-details')),
        hideFromMainMenu: true,
        protected: true,
      },
      {
        path: '/products/safety',
        title: 'Safety',
        name: 'safety',
        feature: Feature.Safety,
        component: React.lazy(() => import('./products/safety/safety')),
        protected: true,
      },
    ],
  },
  {
    path: '/profile',
    title: 'My Profile',
    name: 'myProfile',
    icon: 'icon-profile',
    feature: Feature.Profile,
    hideFromMainMenu: true,
    protected: true,
    component: React.lazy(() => import('./profile/profile')),
    children: [
      {
        path: '/profile/privacy-information',
        title: 'Privacy information',
        name: 'profilePreferences',
        feature: Feature.ProfilePreferences,
        isActive: false,
        protected: true,
        component: React.lazy(() => import('./profile/preferences-and-privacy/privacy-information')),
      },
    ],
  },
  {
    path: '/offers',
    title: 'My Offers',
    name: 'myOffers',
    feature: Feature.Offers,
    component: React.lazy(() => import('./profile/profile')), // TODO: add offers feature component
    icon: 'icon-imi-offers',
    protected: true,
  },
  {
    path: '/fix-errors',
    title: 'Fix Errors',
    name: 'fixError',
    feature: Feature.FixError,
    component: React.lazy(() => import('./fix-error/fix-errors')),
    icon: 'icon-fix-errors',
    protected: true,
  },
  {
    path: '/customer',
    title: 'Customer',
    name: 'customer',
    feature: Feature.Customer,
    component: React.lazy(() => import('./profile/profile')), // TODO: add offers feature component
    hideFromMainMenu: true,
    protected: 'Authorize',
    children: [
      {
        path: '/customer/link',
        title: 'Link Customer',
        name: 'link',
        feature: Feature.Customer,
        component: React.lazy(() => import('./customer/link-customer/link-customer')),
      },
      {
        path: '/customer/create',
        title: 'Create Customer',
        name: 'link',
        feature: Feature.Customer,
        component: React.lazy(() => import('./customer/create-customer/create-customer')),
      },
      {
        path: '/customer/success',
        title: 'Create Customer',
        name: 'link',
        feature: Feature.Customer,
        component: React.lazy(() => import('./customer/create-customer/steps/activate-success')),
      },
    ],
  },
  {
    path: '/add-to-bill/:productCode',
    title: '',
    name: 'add-to-bill',
    feature: Feature.Action,
    component: React.lazy(() => import('./action/action-overlay')),
    protected: true,
    hideFromMainMenu: true,
  },
  {
    path: '/action',
    title: '',
    name: 'action',
    feature: Feature.Action,
    component: React.lazy(() => import('./action/action-overlay')),
    protected: true,
    hideFromMainMenu: true,
  },
  {
    path: '/inbox',
    title: 'Inbox',
    name: 'inbox',
    icon: 'icon-imi-inbox',
    feature: Feature.Inbox,
    component: React.lazy(() => import('./inbox/inbox')),
    protected: true,
    hideFromMainMenu: true,
  },
];